import type { EfacturaSyncItem, EfacturaSyncResult } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Alert, Button, Card, Descriptions, Modal, Popconfirm, Segmented, Space, Spin, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import {
  disconnectEfactura,
  fetchEfacturaAuthUrl,
  fetchEfacturaConnection,
  syncEfacturaFromSpv,
  testEfactura,
  updateEfacturaSettings,
} from './api';

const SYNC_STATUS: Record<EfacturaSyncItem['status'], { color: string; label: string }> = {
  matched: { color: 'green', label: 'Potrivită acum' },
  already_tracked: { color: 'blue', label: 'Deja urmărită' },
  not_found: { color: 'orange', label: 'Fără corespondent local' },
  unreadable: { color: 'red', label: 'Necitibilă' },
};

const SYNC_COLUMNS: ColumnsType<EfacturaSyncItem> = [
  {
    title: 'Nr. factură (SPV)',
    dataIndex: 'invoiceNumber',
    render: (value: string | null) => <span className="num">{value ?? '—'}</span>,
  },
  {
    title: 'Factură locală',
    dataIndex: 'matchedFullNumber',
    render: (value: string | null) => (value ? <span className="num">{value}</span> : <span>—</span>),
  },
  {
    title: 'Index încărcare',
    dataIndex: 'uploadIndex',
    render: (value: string) => <span className="num">{value}</span>,
  },
  {
    title: 'Data',
    dataIndex: 'createdAt',
    render: (value: string | null) => (value ? new Date(value).toLocaleString('ro-RO') : '—'),
  },
  {
    title: 'Rezultat',
    dataIndex: 'status',
    render: (status: EfacturaSyncItem['status']) => <Tag color={SYNC_STATUS[status].color}>{SYNC_STATUS[status].label}</Tag>,
  },
];

/**
 * "e-Factura" tab of the firm drawer (edit mode only). Manages the firm's
 * ANAF/SPV connection: environment (test/prod), OAuth connect/disconnect, a
 * connection test, and the auto-send / auto-refresh toggles. The digital
 * certificate step happens in the user's browser on logincert.anaf.ro — the
 * "Conectează" button just redirects there.
 */
export function FirmaEfacturaTab({ firmaId }: { firmaId: string }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [syncResult, setSyncResult] = useState<EfacturaSyncResult | null>(null);

  const connectionKey = ['firms', 'efactura', firmaId];
  const { data: status, isLoading } = useQuery({
    queryKey: connectionKey,
    queryFn: () => fetchEfacturaConnection(firmaId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: connectionKey });
    // Refresh the header firm selector's badges too.
    queryClient.invalidateQueries({ queryKey: ['efactura', 'connections'] });
  };

  const connectMutation = useMutation({
    mutationFn: () => fetchEfacturaAuthUrl(firmaId),
    onSuccess: (url) => {
      // Full-page redirect to ANAF so the user can present their certificate.
      window.location.href = url;
    },
    onError: (error) => {
      message.error(getApiError(error)?.message ?? 'Nu s-a putut porni conectarea la ANAF.');
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectEfactura(firmaId),
    onSuccess: () => {
      message.success('Firma a fost deconectată de la ANAF.');
      invalidate();
    },
    onError: (error) => message.error(getApiError(error)?.message ?? 'Deconectarea a eșuat.'),
  });

  const testMutation = useMutation({
    mutationFn: () => testEfactura(firmaId),
    onSuccess: (result) => {
      if (result.ok) message.success('Conexiune ANAF validă.');
      else message.warning('Conexiunea ANAF nu este validă — reconectează-te.');
    },
    onError: (error) => message.error(getApiError(error)?.message ?? 'Testul conexiunii a eșuat.'),
  });

  const settingsMutation = useMutation({
    mutationFn: (input: Parameters<typeof updateEfacturaSettings>[1]) => updateEfacturaSettings(firmaId, input),
    onSuccess: () => invalidate(),
    onError: (error) => message.error(getApiError(error)?.message ?? 'Setarea nu a putut fi salvată.'),
  });

  const syncMutation = useMutation({
    mutationFn: () => syncEfacturaFromSpv(firmaId, 60),
    onSuccess: (result) => {
      setSyncResult(result);
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error) => message.error(getApiError(error)?.message ?? 'Sincronizarea din SPV a eșuat.'),
  });

  if (isLoading || !status) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {!status.enabled && (
        <Alert
          type="warning"
          showIcon
          message="Integrarea e-Factura este dezactivată"
          description="Pornește-o din configurarea serverului (EFACTURA_ENABLED=true) pentru a putea conecta firma la ANAF."
        />
      )}

      <Card size="small" title="Conexiune ANAF SPV">
        <Descriptions column={1} size="small" styles={{ label: { width: 140 } }}>
          <Descriptions.Item label="Stare">
            {status.connected ? (
              <Tag color="green">Conectat</Tag>
            ) : status.expired ? (
              <Tag color="volcano">Expirat — reconectează</Tag>
            ) : (
              <Tag>Neconectat</Tag>
            )}
          </Descriptions.Item>
          {status.spvUsername && <Descriptions.Item label="Utilizator SPV">{status.spvUsername}</Descriptions.Item>}
          {status.expiresAt && (
            <Descriptions.Item label="Token expiră">
              {new Date(status.expiresAt).toLocaleString('ro-RO')}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Space wrap style={{ marginTop: 12 }}>
          <Button
            type="primary"
            disabled={!status.enabled}
            loading={connectMutation.isPending}
            onClick={() => connectMutation.mutate()}
          >
            {status.connected || status.expired ? 'Reconectează' : 'Conectează'}
          </Button>
          <Button
            disabled={!status.enabled || (!status.connected && !status.expired)}
            loading={testMutation.isPending}
            onClick={() => testMutation.mutate()}
          >
            Testează conexiunea
          </Button>
          {(status.connected || status.expired || status.spvUsername) && (
            <Popconfirm title="Deconectezi firma de la ANAF?" onConfirm={() => disconnectMutation.mutate()}>
              <Button danger loading={disconnectMutation.isPending}>
                Deconectează
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Card>

      <Card size="small" title="Setări">
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Typography.Text strong>Mediu ANAF</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Segmented
                value={status.environment}
                options={[
                  { label: 'Test', value: 'test' },
                  { label: 'Producție', value: 'prod' },
                ]}
                onChange={(value) => settingsMutation.mutate({ environment: value as 'test' | 'prod' })}
              />
              <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
                Începe pe „Test"; comută pe „Producție" doar după validarea fluxului.
              </Typography.Paragraph>
            </div>
          </div>

          <Space align="start" size={12}>
            <Switch
              checked={status.autoSend}
              loading={settingsMutation.isPending}
              onChange={(checked) => settingsMutation.mutate({ autoSend: checked })}
            />
            <div>
              <Typography.Text strong>Trimite automat la emitere</Typography.Text>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Facturile emise pentru această firmă se trimit automat la e-Factura.
              </Typography.Paragraph>
            </div>
          </Space>

          <Space align="start" size={12}>
            <Switch
              checked={status.autoRefresh}
              loading={settingsMutation.isPending}
              onChange={(checked) => settingsMutation.mutate({ autoRefresh: checked })}
            />
            <div>
              <Typography.Text strong>Reînnoire automată token</Typography.Text>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Token-ul ANAF se reînnoiește automat înainte de expirare.
              </Typography.Paragraph>
            </div>
          </Space>
        </Space>
      </Card>

      <Card size="small" title="Sincronizare din SPV">
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Preia din SPV facturile deja trimise (ex. dintr-un alt sistem) din ultimele 60 de zile și le marchează ca
          validate, completând indexii ANAF. Potrivirea se face după numărul facturii.
        </Typography.Paragraph>
        <Button
          disabled={!status.enabled || !status.connected}
          loading={syncMutation.isPending}
          onClick={() => syncMutation.mutate()}
        >
          Sincronizează ultimele 60 de zile
        </Button>
      </Card>

      <Modal
        open={syncResult !== null}
        title="Rezultat sincronizare SPV"
        width={880}
        onCancel={() => setSyncResult(null)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setSyncResult(null)}>
            Închide
          </Button>,
        ]}
      >
        {syncResult ? (
          <>
            <Typography.Paragraph>
              {syncResult.total} documente trimise în SPV · <strong>{syncResult.matched}</strong> potrivite acum ·{' '}
              {syncResult.unmatched} nepotrivite.
            </Typography.Paragraph>
            {syncResult.items.length === 0 ? (
              <Typography.Text type="secondary">
                Niciun document trimis în intervalul selectat (ultimele 60 de zile — atât păstrează SPV).
              </Typography.Text>
            ) : (
              <>
                <Table<EfacturaSyncItem>
                  rowKey={(row) => row.uploadIndex}
                  size="small"
                  columns={SYNC_COLUMNS}
                  dataSource={syncResult.items}
                  pagination={false}
                  scroll={{ y: 420 }}
                />
                <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                  „Deja urmărită" = factura există local dar avea deja un status e-Factura (nu a fost suprascrisă).
                  „Fără corespondent local" = e în SPV dar nu există local o factură cu acest număr.
                </Typography.Paragraph>
              </>
            )}
          </>
        ) : null}
      </Modal>
    </Space>
  );
}
